import { type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type ContentAssignmentDTO, ContentLocalizationStatus, ContentPlacement, ContentScopeType } from '@today/types'
import type { AssignmentListViewProps, AssignmentRowView, AttachDialogViewProps, PlacementView } from '@today/ui-domain'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { contentService } from '@/services/content.service'
import { type InheritedAssignment, nextSortOrder, pickerCandidates, reorder, replaceOptions } from './assignment-rules'
import { contentKeys, useContentT, useContentViewer } from './useContentQueries'

/** Invalidate everything an assignment change can affect ("used by", pickers, product views). */
function useReloadAssignments() {
	const client = useQueryClient()
	return () =>
		Promise.all([
			client.invalidateQueries({ queryKey: contentKeys.assignments }),
			client.invalidateQueries({ queryKey: ['content', 'product-texts'] }),
			client.invalidateQueries({ queryKey: contentKeys.texts }),
		])
}

/** Reorder is the only optimistic write: PATCH the changed rows, restore the snapshot on failure (REQ-012). */
function useReorder(queryKey: QueryKey) {
	const client = useQueryClient()
	const reload = useReloadAssignments()
	return useMutation({
		mutationFn: ({ patches }: { patches: { id: string; sort_order: number }[]; previous: unknown }) =>
			Promise.all(patches.map((p) => contentService.updateAssignment(p.id, { sort_order: p.sort_order }))),
		onError: (_error, { previous }) => {
			client.setQueryData(queryKey, previous)
		},
		onSettled: () => reload(),
	})
}

/** Per row id, the inherited texts that row may replace. */
const replaceOptionsByRow = (source: readonly ContentAssignmentDTO[], inherited: readonly InheritedAssignment[]) =>
	Object.fromEntries(
		source.map((a) => [a.id, replaceOptions(inherited, a.textId).map((i) => ({ id: i.textId, label: i.textCode }))]),
	)

/** Placement/replaces PATCH and detach (with its confirmation) of single rows. */
function useRowEdits() {
	const reload = useReloadAssignments()
	const [detaching, setDetaching] = useState<string | null>(null)
	const update = useMutation({
		mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
			contentService.updateAssignment(id, body),
		onSettled: () => reload(),
	})
	const detach = useMutation({
		mutationFn: (id: string) => contentService.removeAssignment(id),
		onSuccess: async () => {
			setDetaching(null)
			await reload()
		},
	})
	return { update, detach, detaching, setDetaching }
}

/**
 * An ordered assignment list (TO-420 content-admin-ui REQ-010/REQ-012): reorder
 * by drag or Move up/down, placement/replaces edits (product scope), detach.
 */
export function useAssignmentList(args: {
	label: string
	rows: AssignmentRowView[]
	source: ContentAssignmentDTO[]
	queryKey: QueryKey
	write: (data: unknown, ordered: ContentAssignmentDTO[]) => unknown
	product: boolean
	inherited?: InheritedAssignment[]
}): AssignmentListViewProps {
	const { t, errorMessage } = useContentT()
	const navigate = useNavigate()
	const client = useQueryClient()
	const { caps } = useContentViewer()
	const reorderMutation = useReorder(args.queryKey)
	const { update, detach, detaching, setDetaching } = useRowEdits()
	const onMove = (from: number, to: number) => {
		// One reorder at a time: a failed one restores its own snapshot, which would undo a later move.
		if (reorderMutation.isPending) return
		const { ordered, patches } = reorder(args.source, from, to)
		if (patches.length === 0) return
		const previous = client.getQueryData(args.queryKey)
		client.setQueryData(args.queryKey, (data: unknown) => args.write(data, ordered))
		reorderMutation.mutate({ patches, previous })
	}
	const target = args.source.find((a) => a.id === detaching)
	const failure = reorderMutation.error ?? update.error
	return {
		t,
		label: args.label,
		rows: args.rows,
		product: args.product,
		replaceOptions: replaceOptionsByRow(args.source, args.inherited ?? []),
		canEdit: caps.assign,
		detaching: target ? { code: target.textCode, error: detach.error ? errorMessage(detach.error) : null } : null,
		error: failure ? errorMessage(failure) : null,
		isBusy: reorderMutation.isPending || update.isPending || detach.isPending,
		onMove,
		onPlacement: (id, placement: PlacementView) => update.mutate({ id, body: { placement } }),
		onReplaces: (id, textId) => update.mutate({ id, body: { replaces_text_id: textId || null } }),
		onDetach: (id) => {
			detach.reset()
			setDetaching(id)
		},
		onOpen: (textId) => void navigate(`/content/texts/${textId}`),
		onConfirmDetach: () => detaching && detach.mutate(detaching),
		onCancelDetach: () => setDetaching(null),
	}
}

type AttachScope = { type: ContentScopeType.TENANT } | { type: ContentScopeType.PRODUCT; productId: string }

/** `POST /texts/assignments` body; the new row goes last (`nextSortOrder`). */
export const attachBody = (
	scope: AttachScope,
	values: { textId: string; placement: PlacementView; replacesTextId: string },
	existing: readonly ContentAssignmentDTO[],
): Record<string, unknown> => {
	const sort_order = nextSortOrder(existing)
	if (scope.type === ContentScopeType.TENANT)
		return { text_id: values.textId, scope_type: ContentScopeType.TENANT, sort_order }
	return {
		text_id: values.textId,
		scope_type: ContentScopeType.PRODUCT,
		scope_id: scope.productId,
		placement: values.placement,
		...(values.replacesTextId ? { replaces_text_id: values.replacesTextId } : {}),
		sort_order,
	}
}

/** Picker candidates: texts with a published or approved localization matching the search, minus `existing`. */
function usePicker(isOpen: boolean, search: string, existing: readonly ContentAssignmentDTO[]) {
	const filter = (status: string) => ({ 'filters[status]': status, ...(search ? { 'filters[q]': search } : {}) })
	const published = useQuery({
		queryKey: [...contentKeys.texts, 'picker', ContentLocalizationStatus.PUBLISHED, search] as const,
		queryFn: () => contentService.allTexts(filter(ContentLocalizationStatus.PUBLISHED)),
		enabled: isOpen,
	})
	const approved = useQuery({
		queryKey: [...contentKeys.texts, 'picker', ContentLocalizationStatus.APPROVED, search] as const,
		queryFn: () => contentService.allTexts(filter(ContentLocalizationStatus.APPROVED)),
		enabled: isOpen,
	})
	return pickerCandidates(published.data ?? [], approved.data ?? [], new Set(existing.map((a) => a.textId))).map(
		(x) => ({ id: x.id, label: `${x.code} · ${x.name}` }),
	)
}

const EMPTY_ATTACH = { textId: '', placement: ContentPlacement.WITH_PRODUCT as PlacementView, replacesTextId: '' }

/** The attach dialog (TO-420 content-admin-ui REQ-010/REQ-012): picker, placement and replacement for products. */
export function useAttachDialog(args: {
	scope: AttachScope
	existing: ContentAssignmentDTO[]
	inherited?: InheritedAssignment[]
}): AttachDialogViewProps & { open: () => void } {
	const { t, errorMessage } = useContentT()
	const reload = useReloadAssignments()
	const [isOpen, setOpen] = useState(false)
	const [search, setSearch] = useState('')
	const [values, setValues] = useState(EMPTY_ATTACH)
	const candidates = usePicker(isOpen, search, args.existing)
	const create = useMutation({
		mutationFn: (body: Record<string, unknown>) => contentService.createAssignment(body),
		onSuccess: async () => {
			setOpen(false)
			await reload()
		},
	})
	return {
		t,
		isOpen,
		product: args.scope.type === ContentScopeType.PRODUCT,
		search,
		candidates,
		...values,
		replaceOptions: replaceOptions(args.inherited ?? []).map((i) => ({ id: i.textId, label: i.textCode })),
		error: create.error ? errorMessage(create.error) : null,
		isBusy: create.isPending,
		onSearch: setSearch,
		onChange: (field, value) => setValues((v) => ({ ...v, [field]: value })),
		onConfirm: () => create.mutate(attachBody(args.scope, values, args.existing)),
		onCancel: () => setOpen(false),
		open: () => {
			create.reset()
			setSearch('')
			setValues(EMPTY_ATTACH)
			setOpen(true)
		},
	}
}
